import { AnimatePresence, domAnimation, LazyMotion, m } from 'motion/react';
import { FieldError } from '../LaddboxForm';
import GoogleMap, { type Reseller } from './map';
import type { SelectionMode } from './schema';

export function ResellerField({
  selectionMode,
  selectedReseller,
  mapOpen,
  errors,
  resellersAvailable,
  onGeolocate,
  onOpenMap,
  onSelectReseller,
}: {
  selectionMode: SelectionMode;
  selectedReseller: Reseller | null;
  mapOpen: boolean;
  errors: string[];
  resellersAvailable: boolean;
  onGeolocate: () => void;
  onOpenMap: () => void;
  onSelectReseller: (r: Reseller | null) => void;
}) {
  return (
    <div className="grid">
      <p className="block text-sm font-medium mb-1">Kia-återförsäljare</p>

      {selectionMode === 'idle' && (
        <div className="grid gap-2">
          {resellersAvailable ? (
            <button
              type="button"
              onClick={onGeolocate}
              className="w-full px-4 py-3 flex items-center justify-center gap-2 border border-black text-black text-sm font-medium hover:bg-black hover:text-white transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
              Hitta min närmsta Kia-återförsäljare
            </button>
          ) : (
            <p className="text-sm text-dark-blue/60 px-4 py-3 border border-dark-blue/20 text-center">
              Kia-återförsäljare är inte tillgängliga just nu. Försök igen
              senare.
            </p>
          )}
        </div>
      )}

      {selectionMode === 'locating' && (
        <div className="flex items-center gap-2 text-sm text-darkborder-dark-blue/70 px-4 py-3">
          <span className="animate-spin inline-block">↻</span>
          Söker din plats…
        </div>
      )}

      {selectionMode === 'located' && (
        <div className="grid gap-2">
          <LazyMotion features={domAnimation}>
            <AnimatePresence>
              {mapOpen && (
                <m.div
                  key="map"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <p className="text-sm text-darkborder-dark-blue/70 mb-2">
                    Välj en annan återförsäljare på kartan.
                  </p>
                  <div className="h-[60vh] rounded overflow-hidden">
                    <GoogleMap onSelectReseller={onSelectReseller} />
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </LazyMotion>
          {!mapOpen && selectedReseller && (
            <div className="flex items-center justify-between border border-dark-blue rounded px-4 py-3">
              <span className="text-sm font-medium flex items-center gap-2">
                {selectedReseller.name}
              </span>
              <button
                type="button"
                onClick={onOpenMap}
                className="text-xs text-darkborder-dark-blue/60 underline hover:text-darkborder-dark-blue"
              >
                Ändra
              </button>
            </div>
          )}
        </div>
      )}

      {(selectionMode === 'denied' || selectionMode === 'error') && (
        <div className="grid gap-2">
          <LazyMotion features={domAnimation}>
            <AnimatePresence>
              {mapOpen && (
                <m.div
                  key="map"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <p className="text-sm text-darkborder-dark-blue/70 mb-2">
                    {selectionMode === 'denied'
                      ? 'Du har blockerat platsåtkomst. Välj en återförsäljare på kartan.'
                      : 'Kunde inte hämta din plats. Välj en återförsäljare på kartan.'}
                  </p>
                  <div className="h-[60vh] rounded overflow-hidden">
                    <GoogleMap onSelectReseller={onSelectReseller} />
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </LazyMotion>
          {!mapOpen && selectedReseller && (
            <div className="flex items-center justify-between border border-dark-blue rounded px-4 py-3">
              <span className="text-sm font-medium flex items-center gap-2">
                {selectedReseller.name}
              </span>
              <button
                type="button"
                onClick={onOpenMap}
                className="text-xs text-darkborder-dark-blue/60 underline hover:text-darkborder-dark-blue"
              >
                Ändra
              </button>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-dark-blue/60 font-bold mt-2">
        Här kommer du hämta din Kia EV2 (lånebil) ifall du vinner. Tänk på att
        välja en återförsäljare nära dig.
      </p>

      <FieldError errors={errors} />
    </div>
  );
}
